import type { FolderAccess, FolderHandle, WatchOverrides } from './types';

const DB_NAME = 'thottbot-watch';
const STORE = 'handles';
const KEY = 'root';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

function isFolderHandle(value: unknown): value is FolderHandle {
  const h = value as Partial<FolderHandle> | null;
  return (
    !!h &&
    h.kind === 'directory' &&
    typeof h.values === 'function' &&
    typeof h.queryPermission === 'function'
  );
}

/**
 * The real folder layer: Chromium's File System Access API for the picker and permissions,
 * IndexedDB for remembering the handle between visits (directory handles survive
 * structured cloning; the browser decides whether the grant persisted).
 */
export const browserFolderAccess: FolderAccess = {
  supported: typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function',
  async pick() {
    try {
      const handle = await window.showDirectoryPicker!({ mode: 'read', id: 'thottbot-wow' });
      return handle as unknown as FolderHandle;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return null;
      throw err;
    }
  },
  async remembered() {
    try {
      const stored = await withStore('readonly', (s) => s.get(KEY));
      return isFolderHandle(stored) ? stored : null;
    } catch {
      return null;
    }
  },
  async remember(handle) {
    await withStore('readwrite', (s) => s.put(handle, KEY));
  },
  async forget() {
    await withStore('readwrite', (s) => s.delete(KEY));
  },
};

/** The folder layer and timing to use: the e2e fake when installed, otherwise the browser. */
export function resolveWatchOverrides(): WatchOverrides {
  return typeof window === 'undefined' ? {} : (window.__thottbotWatch ?? {});
}
